import { Divider, Typography, IconButton, CssBaseline } from '@mui/material';

import { GeneralTooltip as Tooltip } from '@repo/components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Edit from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';

import { Paper } from '@mui/material';
import { EventEmitter } from '@repo/shared/helpers';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { getAuthenticatedSession } from 'src/api/authentication';
import { getConfig } from 'src/api/config';
import ProfileLayout from 'src/layouts/profile';
import { ResetTwoFactorForm } from '@repo/forms';
import { TwoFactorForm } from '@repo/forms';
import { UpdatePasswordForm } from '@repo/forms';

type EditDialog = 'password' | 'two-factor';
type ConfirmDialog = 'reset-two-factor';
export const Route = createFileRoute('/profile')({
	component: RouteComponent,
	loader: async () => {
		const authenticatedSession = await getAuthenticatedSession();
		const config = await getConfig();
		return { authenticatedSession, config };
	},
});

function RouteComponent() {
	const router = useRouter();
	const { authenticatedSession, config } = Route.useLoaderData();
	console.log(Route);
	const [editDialog, setEditDialog] = useState<EditDialog | undefined>();
	const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog | undefined>();

	const handleBack = () => {
		if (window.history.length > 1) {
			router.history.back();
		} else {
			router.navigate({ to: '/' });
		}
	};

	const twoFactorRequired = config.twoFactorRequired;
	const openEditDialog = async (name: EditDialog) => setEditDialog(name);
	const closeEditDialog = () => setEditDialog(undefined);
	const openConfirmDialog = async (name: ConfirmDialog) => setConfirmDialog(name);
	const closeConfirmDialog = () => setConfirmDialog(undefined);

	const updatePasswordSuccess = () => {
		EventEmitter.emit('toast', {
			type: 'success',
			message: 'Password succesfully updated',
		});
		setEditDialog(undefined);
		router.invalidate();
	};

	const handleResetTwoFactor = () => {
		closeConfirmDialog();
		router.invalidate();
	};

	const handleConfigureTwoFactor = () => {
		closeEditDialog();
		router.invalidate();
	};

	const user = authenticatedSession.user;
	console.log(user);
	const hasTwoFactor = user?.twoFactor;
	const isSSOUser = authenticatedSession.ssoAvailable && !!user.externalId;
	return (
		<ProfileLayout user={user}>
			<CssBaseline />
			<div style={{ padding: 36, margin: '0 auto', maxWidth: '480px' }}>
				<div className="flex items-center gap-3 mb-4">
					<IconButton
						onClick={handleBack}
						sx={{
							backgroundColor: 'action.hover',
							'&:hover': {
								backgroundColor: 'action.selected',
							},
						}}
					>
						<ArrowBackIcon />
					</IconButton>
					<Typography variant="h4">Profile</Typography>
				</div>
				<Paper variant="outlined" className="my-2 flex flex-col">
					<div className="p-2">
						<Typography variant="subtitle1">Basic information</Typography>
						<Divider sx={{ marginBottom: 1 }} />

						<Typography variant="caption">First name</Typography>
						<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
							{user?.firstName || '-'}
						</Typography>

						<Typography variant="caption">Last name</Typography>
						<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
							{user?.lastName || '-'}
						</Typography>

						<Typography variant="caption">Email</Typography>
						<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
							{user?.email || '-'}
						</Typography>
					</div>
				</Paper>

				{!isSSOUser && (
					<Paper variant="outlined" className="my-2 flex flex-col">
						<div className="p-2">
							<Typography variant="subtitle1">Credentials</Typography>
							<Divider sx={{ marginBottom: 1 }} />

							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
								}}
							>
								<Typography variant="caption">Password</Typography>
								<Tooltip text="Update password">
									<IconButton size="small" onClick={() => openEditDialog('password')}>
										<Edit fontSize="small" />
									</IconButton>
								</Tooltip>
							</div>
							<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
								********
							</Typography>
							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
								}}
							>
								<Typography variant="caption">Two Factor</Typography>
								{hasTwoFactor && !twoFactorRequired ? (
									<Tooltip text="Reset Two Factor Authentication">
										<IconButton size="small" onClick={() => openConfirmDialog('reset-two-factor')}>
											<RestoreIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								) : (
									<Tooltip text="Configure Two Factor">
										<IconButton size="small" onClick={() => openEditDialog('two-factor')}>
											<Edit fontSize="small" />
										</IconButton>
									</Tooltip>
								)}
							</div>
							<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
								{hasTwoFactor ? '********' : 'Not set'}
							</Typography>
						</div>
					</Paper>
				)}

				<UpdatePasswordForm
					isOpen={editDialog === 'password'}
					onClose={closeEditDialog}
					onSuccess={updatePasswordSuccess}
				/>
				<TwoFactorForm
					isOpen={editDialog === 'two-factor'}
					openedByUser={true}
					onClose={closeEditDialog}
					onSuccess={handleConfigureTwoFactor}
				/>
				<ResetTwoFactorForm
					isOpen={confirmDialog === 'reset-two-factor'}
					onClose={closeConfirmDialog}
					onSuccess={handleResetTwoFactor}
				/>
			</div>
		</ProfileLayout>
	);
}
